// TarifsPage.js - Pricing page component
// UPDATED: Now imports tariff data from tarifsData.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiClock, FiUsers, FiCamera, FiPrinter, FiShare2, FiStar, FiArrowRight, FiInfo, FiImage, FiMusic, FiLayers } from 'react-icons/fi';

// Import tariff data from separate file
import { 
  DEFAULT_PHOTOBOOTH_TARIFS, 
  DEFAULT_VIDEOBOOTH_100CM_TARIFS, 
  DEFAULT_VIDEOBOOTH_TARIFS,
  DEFAULT_LIVREOR_TARIFS 
} from './tariffData';

// INLINE 80cm tariffs to bypass CDN cache issue
const DEFAULT_VIDEOBOOTH_80CM_TARIFS = [
  {
    id: '360-standard',
    name: 'La 360 Standard',
    duration: '2h',
    price: 349,
    subtitle: 'Plateforme 80cm - de 1 à 3 personnes',
    popular: false,
    color: '#C0C0C0',
    showRibbon: false,
    ribbonText: '',
    ribbonColor: '#DC2626',
    headerColor: '#1a1a1a',
    backgroundType: 'solid',
    backgroundColor: '#ffffff',
    features: [
      'Vidéos 360° illimitées',
      '1 modèle personnalisé inclus',
      '1 musique au choix',
      'Galerie vidéo sécurisée',
      'Animateurs présents sur place',
      'Partage instantané (QR/AirDrop/SMS/Mail)',
      'Accessoires + pistolets à bulles',
      'Livraison, installation et désinstallation',
      'Frais kilométriques facturés au-delà de 25km de nos entrepôts',
      'Assistance 24/24 & 7/7',
    ],
    summary: '1 modèle, 1 musique',
  },
  {
    id: '360-gala',
    name: 'La 360 Gala',
    duration: '3h',
    price: 399,
    subtitle: 'Plateforme 80cm - de 1 à 3 personnes',
    popular: false,
    color: '#B87333',
    showRibbon: false,
    ribbonText: '',
    ribbonColor: '#DC2626',
    headerColor: '#C9A45C',
    backgroundType: 'solid',
    backgroundColor: '#ffffff',
    features: [
      'Vidéos 360° illimitées',
      '1 modèle personnalisé inclus',
      '1 musique au choix',
      'Galerie vidéo sécurisée',
      'Animateurs présents sur place',
      'Partage instantané (QR/AirDrop/SMS/Mail)',
      'Accessoires + pistolets à bulles + pistolets à billets',
      'Livraison, installation et désinstallation',
      'Frais kilométriques facturés au-delà de 25km de nos entrepôts',
      'Assistance 24/24 & 7/7',
    ],
    summary: '1 modèle, 1 musique',
  },
  {
    id: '360-prestige',
    name: 'La 360 Prestige',
    duration: '4h',
    price: 449,
    subtitle: 'Plateforme 80cm - de 1 à 3 personnes',
    popular: false,
    color: '#CFB53B',
    showRibbon: false,
    ribbonText: '',
    ribbonColor: '#DC2626',
    headerColor: '#1a1a1a',
    backgroundType: 'solid',
    backgroundColor: '#ffffff',
    features: [
      'Vidéos 360° illimitées',
      '1 modèle personnalisé inclus',
      '1 musique au choix',
      'Galerie vidéo sécurisée',
      'Animateurs présents sur place',
      'Partage instantané (QR/AirDrop/SMS/Mail)',
      'Accessoires + pistolets à bulles + pistolets à billets + ventilateur studio',
      'Livraison, installation et désinstallation',
      'Frais kilométriques facturés au-delà de 25km de nos entrepôts',
      'Assistance 24/24 & 7/7',
    ],
    summary: '1 modèle, 1 musique',
  },
  {
    id: '360-luxe',
    name: 'La 360 Luxe',
    duration: '6h',
    price: 499,
    subtitle: 'Plateforme 80cm - de 1 à 3 personnes',
    popular: true,
    color: '#D4AF37',
    showRibbon: true,
    ribbonText: 'BEST SELLER',
    ribbonColor: '#DC2626',
    headerColor: '#C9A45C',
    backgroundType: 'gradient',
    gradientColors: ['#D4A574', '#C9A45C', '#B8956F'],
    textColorOverride: '#ffffff',
    features: [
      'Vidéos 360° illimitées',
      '2 modèles personnalisés inclus',
      '2 musiques au choix',
      'Galerie vidéo sécurisée',
      'Animateurs présents sur place',
      'Partage instantané (QR/AirDrop/SMS/Mail)',
      'Tous les accessoires + machine à fumée',
      'Livraison, installation et désinstallation',
      'Frais kilométriques facturés au-delà de 25km de nos entrepôts',
      'Assistance 24/24 & 7/7',
    ],
    summary: '2 modèles, 2 musiques',
  },
];

// Options additionnelles - Based on original site
// Note: Suppression de "Heure supplémentaire 50€" et "Clé USB" pour Livre d'Or et VideoBooth
const DEFAULT_OPTIONS = [
  { name: 'Modèle supplémentaire', price: '+15€', icon: FiCamera },
  { name: 'Clé USB avec photos brutes', price: '+20€', icon: FiPrinter },
];

// Options pour VideoBooth (uniquement modèle supplémentaire)
const VIDEOBOOTH_OPTIONS = [
  { name: 'Modèle supplémentaire', price: '+15€', icon: FiCamera },
];

// Options pour Livre d'Or (aucune option)
const LIVREOR_OPTIONS = [];

// Messages informatifs
const INFO_MESSAGES = {
  reservation: 'Toute demande de réservation fera l\'objet d\'un devis personnalisé.',
  livraison: 'Livraison incluse Istres et ses alentours.',
  fraisKm: 'Frais kilométriques facturés au-delà de 25km de nos entrepôts.',
  caution: 'Chèque de caution de 2 000€ (non encaissé) demandé le jour de l\'événement.',
};

export default function TarifsPage({ pageData, onNavigate, onBookingClick, serviceType = 'photobooth' }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [videoboothSize, setVideoboothSize] = useState('80cm'); // For videobooth size toggle
  
  // Get content from pageData or use defaults
  const content = pageData?.content || {};
  const heroContent = content.hero || {};
  const pricingContent = content.pricing || {};
  const optionsContent = content.options || {};

  // Determine service-specific defaults
  const serviceDefaults = {
    photobooth: {
      title: 'PhotoBooth - Nos Tarifs',
      subtitle: 'Des formules adaptées à tous vos événements',
      tarifs: DEFAULT_PHOTOBOOTH_TARIFS,
    },
    'videobooth-360': {
      title: 'VideoBooth 360° - Nos Tarifs',
      subtitle: 'L\'expérience 360° à partir de 349€',
      // Tarifs depend on selected size
      tarifs80cm: DEFAULT_VIDEOBOOTH_80CM_TARIFS,
      tarifs100cm: DEFAULT_VIDEOBOOTH_100CM_TARIFS,
    },
    'livre-dor': {
      title: 'Livre d\'Or Vidéo - Nos Tarifs',
      subtitle: 'Des souvenirs vivants et inoubliables',
      tarifs: DEFAULT_LIVREOR_TARIFS,
    },
  };

  const defaults = serviceDefaults[serviceType] || serviceDefaults.photobooth;
  
  const title = heroContent.title || defaults.title;
  const subtitle = heroContent.subtitle || defaults.subtitle;
  
  // For VideoBooth 360, switch tarifs based on selected size
  // This handles both default tarifs AND custom tarifs from pageData
  // IMPORTANT: Deep clone to avoid mutations + ALWAYS use default styles
  let tarifs;
  if (serviceType === 'videobooth-360') {
    // Get the appropriate default plans
    const defaultPlans = videoboothSize === '100cm' ? defaults.tarifs100cm : defaults.tarifs80cm;
    // Always use default plans with their styles
    tarifs = JSON.parse(JSON.stringify(defaultPlans));
  } else {
    // For Photobooth and Livre d'Or - always use defaults with styles
    tarifs = JSON.parse(JSON.stringify(defaults.tarifs));
  }
  
  // Get options based on service type:
  // - PhotoBooth: Modèle supplémentaire + Clé USB avec photos brutes
  // - VideoBooth: Modèle supplémentaire + Musique supplémentaire (pas de clé USB, pas d'heure supp)
  // - Livre d'Or: Aucune option
  let options;
  if (serviceType === 'livre-dor') {
    options = LIVREOR_OPTIONS; // Empty - no options for Livre d'Or
  } else if (serviceType === 'videobooth-360') {
    options = VIDEOBOOTH_OPTIONS; // Modèle + Musique (no USB, no heure supp)
  } else {
    options = optionsContent.items || DEFAULT_OPTIONS; // PhotoBooth: Modèle + Clé USB
  }

  // Get hero video from product data if available, otherwise use defaults
  // heroContent.videoId can come from product.hero_video_id in the database
  const defaultVideoIds = {
    'photobooth': '_DcxsNgZTx8', // Default Photobooth video
    'videobooth-360': '_DcxsNgZTx8', // Default VideoBooth video 
    'livre-dor': '_DcxsNgZTx8', // Default Livre d'Or video
  };

  const videoId = heroContent.videoId || heroContent.hero_video_id || defaultVideoIds[serviceType] || '_DcxsNgZTx8';
  const videoUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;

  return (
    <div className="tarifs-page bg-black" data-testid={`${serviceType}-tarifs-page`}>
      {/* Hero Section with Video Background - Full Width */}
      <section className="relative py-20 md:py-28 overflow-hidden min-h-[400px]">
        {/* Video Background - Full Width, No Overflow */}
        <div className="absolute inset-0 z-0 w-screen left-1/2 -translate-x-1/2">
          <div className="absolute inset-0 w-full h-full overflow-hidden">
            <iframe
              src={videoUrl}
              title="Background Video"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none scale-150"
              style={{ 
                border: 'none',
                width: '100vw',
                height: '100vh',
                minWidth: '177.78vh',
                minHeight: '100vh'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {/* Black Overlay 60% */}
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-[#e6d28e] font-medium"
          >
            {subtitle}
          </motion.p>
          
          {/* Size Toggle for VideoBooth */}
          {serviceType === 'videobooth-360' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-8 flex justify-center"
            >
              <div className="inline-flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <button
                  onClick={() => setVideoboothSize('80cm')}
                  className={`px-6 py-2 rounded-full font-semibold transition-all ${
                    videoboothSize === '80cm'
                      ? 'bg-[#e6d28e] text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  80cm (3 pers. max)
                </button>
                <button
                  onClick={() => setVideoboothSize('100cm')}
                  className={`px-6 py-2 rounded-full font-semibold transition-all ${
                    videoboothSize === '100cm'
                      ? 'bg-[#e6d28e] text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  100cm (5 pers. max)
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="w-full h-16">
            <path fill="#000000" d="M421.9,6.5c22.6-2.5,51.5,0.4,75.5,5.3c23.6,4.9,70.9,23.5,100.5,35.7c75.8,32.2,133.7,44.5,192.6,49.7c23.6,2.1,48.7,3.5,103.4-2.5c54.7-6,106.2-25.6,106.2-25.6V100H0V30.3c0,0,72,32.6,158.4,30.5c39.2-0.7,92.8-6.7,134-22.4c21.2-8.1,52.2-18.2,79.7-24.2C399.3,7.9,411.6,7.5,421.9,6.5z"></path>
          </svg>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`grid gap-8 ${
            tarifs.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 
            tarifs.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : // Centré pour 2 formules (Livre d'Or)
            tarifs.length === 3 ? 'md:grid-cols-3' : 
            'md:grid-cols-2 lg:grid-cols-4'
          }`}>
            {tarifs.map((plan, idx) => {
              // Use structured data if available, otherwise parse from features
              const hasStructuredData = plan.prints_count !== undefined || plan.templates_count !== undefined;
              
              // Extract key numbers from features - improved parsing (fallback)
              const getKeyDetail = (keywords, includeUnlimited = true) => {
                for (const feature of plan.features || []) {
                  const lowerFeature = feature.toLowerCase();
                  for (const keyword of keywords) {
                    if (lowerFeature.includes(keyword)) {
                      const match = feature.match(/(\d+)/);
                      if (match) return { value: match[1], unlimited: false };
                      if (includeUnlimited && lowerFeature.includes('illimité')) return { value: '∞', unlimited: true };
                    }
                  }
                }
                return null;
              };
              
              // Use structured data or fallback to parsing
              let photosValue, modelesValue, musiquesValue, hasKmOfferts, hasUsbKey;
              
              if (hasStructuredData) {
                // Structured data from API
                // Check for prints_count === 0 explicitly for "SANS IMPRESSION"
                if (plan.prints_count === 0) {
                  photosValue = '-';
                } else {
                  photosValue = plan.prints_unlimited ? '∞' : (plan.prints_count || '-');
                }
                modelesValue = plan.templates_count || 1;
                musiquesValue = plan.music_count || null;
                hasKmOfferts = plan.km_offerts || false;
                hasUsbKey = plan.usb_included || false;
              } else {
                // Fallback: Parse from features text
                const photosDetail = getKeyDetail(['photo imprimée', 'photos imprimées', 'impression']);
                const modelesDetail = getKeyDetail(['modèle personnalisé', 'modèles personnalisés', 'template']);
                const musiquesDetail = getKeyDetail(['musique', 'musiques']);
                const videosDetail = getKeyDetail(['vidéo', 'vidéos'], true);
                const hasUnlimitedVideos = plan.features?.some(f => f.toLowerCase().includes('vidéos en illimité'));
                
                // Check if this plan has "SANS IMPRESSION" in summary
                if (plan.summary === 'SANS IMPRESSION') {
                  photosValue = '-';
                } else {
                  photosValue = photosDetail?.value || (hasUnlimitedVideos ? '∞' : videosDetail?.value || '-');
                }
                modelesValue = modelesDetail?.value || '1';
                musiquesValue = musiquesDetail?.value || null;
                hasKmOfferts = plan.features?.some(f => f.toLowerCase().includes('frais kilométriques offerts') || f.toLowerCase().includes('frais km offerts'));
                hasUsbKey = plan.features?.some(f => f.toLowerCase().includes('clé usb'));
              }
              
              // Determine if this is a video-based product (360, livre d'or)
              const isVideoProduct = serviceType === 'videobooth-360' || serviceType === 'livre-dor';
              
              // Color schemes for each tier - matching reference site exactly
              // Only the card marked as "popular" gets the golden gradient background
              const tierColorSchemes = [
                { header: '#1a1a1a', accent: '#1a1a1a', headerText: '#ffffff' }, // 1st - Noir
                { header: '#C9A45C', accent: '#C9A45C', headerText: '#ffffff' }, // 2nd - Doré
                { header: '#1a1a1a', accent: '#1a1a1a', headerText: '#ffffff' }, // 3rd - Noir
                { header: '#C9A45C', accent: '#C9A45C', headerText: '#ffffff' }, // 4th - Doré
              ];
              
              // Get color scheme for this tier (cycle through if more than 4 plans)
              const colorScheme = tierColorSchemes[idx % tierColorSchemes.length];
              
              // ===== NEW CONFIGURABLE STYLING SYSTEM =====
              // Use plan's custom styling options or fall back to defaults
              const showRibbon = plan.showRibbon === true;
              const ribbonText = plan.ribbonText || 'BEST SELLER';
              const ribbonColor = plan.ribbonColor || '#DC2626';
              const headerColor = plan.headerColor || colorScheme.header;
              const accentColor = plan.color || colorScheme.accent;
              
              // Background: can be 'solid' or 'gradient'
              const backgroundType = plan.backgroundType || 'solid';
              const backgroundColor = plan.backgroundColor || '#ffffff';
              const gradientColors = plan.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
              
              // Check if card has gradient background (special styling needed)
              const hasGradientBg = backgroundType === 'gradient';
              
              // Text color: override if specified, or white for gradient, dark for solid
              const textColorOverride = plan.textColorOverride;
              const cardTextColor = textColorOverride || (hasGradientBg ? '#ffffff' : '#374151');
              const priceTextColor = textColorOverride || (hasGradientBg ? '#ffffff' : '#1a1a1a');
              
              // Build card background style
              const cardBackground = hasGradientBg 
                ? `linear-gradient(180deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 50%, ${gradientColors[2]} 100%)`
                : backgroundColor;
              
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`relative rounded-2xl shadow-xl overflow-hidden flex flex-col ${
                    hasGradientBg 
                      ? 'ring-2 lg:scale-105 z-10' 
                      : 'ring-1 ring-gray-200'
                  }`}
                  style={{
                    '--tw-ring-color': hasGradientBg ? accentColor : undefined,
                    background: cardBackground
                  }}
                >
                  {/* Header with colored background - for solid bg cards only */}
                  {!hasGradientBg && (
                    <div 
                      className="p-6 text-center relative"
                      style={{ backgroundColor: headerColor }}
                    >
                      <h3 className="text-xl font-bold mb-2 text-white">{plan.name}</h3>
                      <div className="flex items-center justify-center gap-2 text-white/80">
                        <FiClock className="w-4 h-4" />
                        <span className="font-medium">{plan.duration}</span>
                      </div>
                      {plan.subtitle && (
                        <p className="text-sm text-white/60 mt-1">{plan.subtitle}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Gradient cards have title inside the gradient body */}
                  {hasGradientBg && (
                    <div className="p-6 text-center relative">
                      <h3 className="text-xl font-bold mb-2" style={{ color: cardTextColor }}>{plan.name}</h3>
                      <div className="flex items-center justify-center gap-2" style={{ color: `${cardTextColor}CC` }}>
                        <FiClock className="w-4 h-4" />
                        <span className="font-medium">{plan.duration}</span>
                      </div>
                      {plan.subtitle && (
                        <p className="text-sm mt-1" style={{ color: `${cardTextColor}99` }}>{plan.subtitle}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Corner Ribbon - Configurable */}
                  {showRibbon && (
                    <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden z-10">
                      <div 
                        className="absolute w-36 text-center py-1 text-[9px] font-bold uppercase tracking-wider text-white shadow-md"
                        style={{ 
                          backgroundColor: ribbonColor,
                          transform: 'rotate(45deg)',
                          top: '18px',
                          right: '-36px',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
                        }}
                      >
                        {ribbonText}
                      </div>
                    </div>
                  )}

                  {/* Price */}
                  <div className={`p-6 text-center ${!hasGradientBg ? 'border-b border-gray-200' : ''}`}>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold" style={{ color: priceTextColor }}>{plan.price}</span>
                      <span className="text-2xl" style={{ color: hasGradientBg ? `${cardTextColor}AA` : '#9ca3af' }}>€</span>
                    </div>
                    <p className="text-sm mt-2" style={{ color: hasGradientBg ? `${cardTextColor}AA` : '#6b7280' }}>TTC</p>
                  </div>

                  {/* Key Numbers - Highlighted with Icons */}
                  <div 
                    className="p-4"
                    style={{ 
                      background: hasGradientBg ? 'rgba(0,0,0,0.1)' : `linear-gradient(90deg, ${headerColor}15 0%, ${headerColor}05 100%)`
                    }}
                  >
                    <div className="grid grid-cols-3 gap-3">
                      {/* Photos/Vidéos */}
                      <div className="text-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: hasGradientBg ? 'rgba(255,255,255,0.2)' : `${headerColor}20` }}>
                          {isVideoProduct ? <FiCamera className="w-4 h-4" style={{ color: hasGradientBg ? cardTextColor : headerColor }} /> : <FiImage className="w-4 h-4" style={{ color: hasGradientBg ? cardTextColor : headerColor }} />}
                        </div>
                        <div className="text-lg font-bold" style={{ color: hasGradientBg ? cardTextColor : headerColor }}>
                          {plan.summary === 'SANS IMPRESSION' ? '-' : (isVideoProduct ? '∞' : photosValue)}
                        </div>
                        <div className="text-[9px] uppercase tracking-wide leading-tight" style={{ color: hasGradientBg ? `${cardTextColor}CC` : '#4b5563' }}>
                          {plan.summary === 'SANS IMPRESSION' ? 'SANS IMPRESSION' : (isVideoProduct ? 'Vidéos' : 'Photos')}
                        </div>
                      </div>
                      
                      {/* Modèles */}
                      <div className="text-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: hasGradientBg ? 'rgba(255,255,255,0.2)' : `${headerColor}20` }}>
                          <FiLayers className="w-4 h-4" style={{ color: hasGradientBg ? cardTextColor : headerColor }} />
                        </div>
                        <div className="text-lg font-bold" style={{ color: hasGradientBg ? cardTextColor : headerColor }}>
                          {modelesValue}
                        </div>
                        <div className="text-[9px] uppercase tracking-wide leading-tight" style={{ color: hasGradientBg ? `${cardTextColor}CC` : '#4b5563' }}>
                          {parseInt(modelesValue) > 1 ? 'Modèles' : 'Modèle'}
                        </div>
                      </div>
                      
                      {/* Musiques ou Bonus */}
                      <div className="text-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: hasGradientBg ? 'rgba(255,255,255,0.2)' : `${headerColor}20` }}>
                          {musiquesValue ? <FiMusic className="w-4 h-4" style={{ color: hasGradientBg ? cardTextColor : headerColor }} /> : <FiStar className="w-4 h-4" style={{ color: hasGradientBg ? cardTextColor : headerColor }} />}
                        </div>
                        <div className="text-lg font-bold" style={{ color: hasGradientBg ? cardTextColor : headerColor }}>
                          {musiquesValue || (hasKmOfferts && hasUsbKey ? '✓✓' : hasKmOfferts ? '✓' : hasUsbKey ? '✓' : '-')}
                        </div>
                        <div className="text-[9px] uppercase tracking-wide leading-tight" style={{ color: hasGradientBg ? `${cardTextColor}CC` : '#4b5563' }}>
                          {musiquesValue 
                            ? (parseInt(musiquesValue) > 1 ? 'Musiques' : 'Musique')
                            : (hasKmOfferts && hasUsbKey 
                                ? 'Frais Km + USB' 
                                : hasKmOfferts 
                                  ? 'Frais Km offerts' 
                                  : hasUsbKey 
                                    ? 'Clé USB offerte' 
                                    : 'Bonus')
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="p-6 flex-grow">
                    <ul className="space-y-2.5">
                      {(plan.features || []).map((feature, fIdx) => {
                        // Check if this feature contains photo count to bold it
                        const photoMatch = feature.match(/^(\d+)\s*(Photos? imprimées?)/i);
                        
                        return (
                          <li key={fIdx} className="flex items-start gap-2.5">
                            <FiCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: hasGradientBg ? cardTextColor : headerColor }} />
                            {photoMatch ? (
                              <span className="text-sm leading-tight" style={{ color: hasGradientBg ? `${cardTextColor}EE` : '#4b5563' }}>
                                <strong className="font-bold" style={{ color: hasGradientBg ? cardTextColor : '#1f2937' }}>{photoMatch[1]} {photoMatch[2]}</strong>
                                {feature.replace(photoMatch[0], '')}
                              </span>
                            ) : (
                              <span className="text-sm leading-tight" style={{ color: hasGradientBg ? `${cardTextColor}EE` : '#4b5563' }}>{feature}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="p-6 pt-0">
                    <button
                      onClick={(e) => {
                        // Pass the full plan info to pre-select in booking modal
                        const formulaInfo = {
                          formulaId: plan.id,
                          formulaName: plan.name,
                          optionName: plan.summary || plan.name,
                          duration: plan.duration,
                          price: plan.price,
                          extras: plan.features || [],
                          allExtras: plan.features || []
                        };
                        onBookingClick?.(e, serviceType, plan.duration, formulaInfo);
                      }}
                      className="w-full py-3 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105 hover:opacity-90"
                      style={{ 
                        backgroundColor: hasGradientBg ? '#ffffff' : headerColor,
                        color: hasGradientBg ? gradientColors[1] : '#ffffff'
                      }}
                      data-testid={`select-plan-${plan.id}`}
                    >
                      Réserver
                      <FiArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Options Section - Only show if there are options for this service */}
      {options.length > 0 && (
        <section className="py-16 bg-zinc-900">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Options supplémentaires
            </h2>
            
            <div className={`grid gap-6 ${
              options.length === 1 ? 'flex justify-center' :
              options.length <= 2 ? 'grid-cols-2 max-w-md mx-auto' : 
              'grid-cols-2 md:grid-cols-4'
            }`}>
              {options.map((option, idx) => {
                const IconComponent = option.icon || FiStar;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                    className="bg-black/50 border border-zinc-700 rounded-xl p-6 text-center hover:border-[#d4af37]/50 transition-all"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-6 h-6 text-[#d4af37]" />
                    </div>
                    <h3 className="font-semibold text-white mb-2 text-sm">{option.name}</h3>
                    <p className="text-[#d4af37] font-bold">{option.price}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Info Section */}
      <section className="py-16 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-black/50 border border-zinc-700 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              Ce qui est inclus dans toutes nos formules
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                  <FiCheck className="w-5 h-5 text-[#d4af37]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Livraison, installation et désinstallation</h3>
                  <p className="text-white/60 text-sm">Notre équipe s'occupe de tout</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                  <FiCheck className="w-5 h-5 text-[#d4af37]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Modèle(s) personnalisé(s)</h3>
                  <p className="text-white/60 text-sm">Créé par nos graphistes selon vos envies</p>
                </div>
              </div>
              
              {/* Hide gallery section for Livre d'Or - no online gallery for this service */}
              {serviceType !== 'livre-dor' && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                    <FiCheck className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Galerie en ligne sécurisée</h3>
                    <p className="text-white/60 text-sm">Accès privé à toutes vos photos/vidéos</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                  <FiCheck className="w-5 h-5 text-[#d4af37]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Assistance 24/24 & 7/7</h3>
                  <p className="text-white/60 text-sm">Une équipe à votre écoute</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Message Section */}
      <section className="py-8 bg-[#d4af37]/10 border-y border-[#d4af37]/20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-center">
            <div className="flex items-center gap-2 text-[#d4af37]">
              <FiInfo className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{INFO_MESSAGES.reservation}</span>
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row flex-wrap justify-center items-center gap-2 md:gap-4 text-sm text-white/70 text-center">
            <span>• {INFO_MESSAGES.livraison}</span>
            <span>• {INFO_MESSAGES.fraisKm}</span>
            <span>• {INFO_MESSAGES.caution}</span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-3xl font-bold text-white mb-4">
            Besoin d'un devis personnalisé ?
          </h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            Vous avez un événement spécial ou des besoins particuliers ? 
            Contactez-nous pour un devis sur mesure.
          </p>
          <button
            onClick={() => onNavigate?.('contact')}
            className="bg-gradient-to-r from-[#d4af37] to-[#b8972e] hover:from-[#b8972e] hover:to-[#a08526] text-black font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            data-testid="cta-devis-btn"
          >
            Demander un devis gratuit
          </button>
        </div>
      </section>
    </div>
  );
}
